import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import CopyableField from './CopyableField';
import OfferPromos from "./OfferPromos";
import OfferCreate from "./OfferCreate";
import OfferEdit from "./OfferEdit";
import CopyToAccountWizard from "./CopyToAccountWizard";
import ImportSharedModal from "./ImportSharedModal";
import CompareAccounts from "./CompareAccounts";
import EmpikOffers from './EmpikOffers';

const API = "/api";

interface Offer {
  id: string; offer_id: string; account_id: string; account_name: string; name: string;
  cross_published?: boolean; cross_target_name?: string;
  sku: string; ean: string; price: number; currency: string;
  stock_available: number; stock_sold: number; status: string;
  handling_time: string; image_url: string; offer_url: string;
  ended_at?: string | null;
}

const HANDLING_OPTIONS = [
  { value: "PT0S", label: "Natychmiast" }, { value: "P1D", label: "1 dzien" },
  { value: "P2D", label: "2 dni" }, { value: "P3D", label: "3 dni" },
  { value: "P5D", label: "5 dni" }, { value: "P7D", label: "7 dni" },
  { value: "P14D", label: "14 dni" }, { value: "P21D", label: "21 dni" },
  { value: "P30D", label: "30 dni" }, { value: "P60D", label: "60 dni" },
];

function parseHandlingHours(ht: string): number {
  const dMatch = ht.match(/P(\d+)D/);
  if (dMatch) return parseInt(dMatch[1]) * 24;
  const hMatch = ht.match(/PT(\d+)H/);
  if (hMatch) return parseInt(hMatch[1]);
  return 0;
}

function handlingLabel(ht: string): string {
  const hours = parseHandlingHours(ht);
  if (hours === 0) return "natychmiast";
  const days = Math.ceil(hours / 24);
  return days + (days === 1 ? " dzien" : " dni");
}

function handlingDate(ht: string): string {
  const hours = parseHandlingHours(ht);
  if (hours === 0) return "";
  const days = Math.ceil(hours / 24);
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function isDelayed(ht: string): boolean { return !!ht && ht !== "PT0S"; }

interface OffersProps { onBack?: () => void; currentUser?: any; }

export default function Offers({ onBack, currentUser = null }: OffersProps) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [stockThreshold, setStockThreshold] = useState<number>(20);
  const [lowStockCount, setLowStockCount] = useState<number>(0);
  const [total, setTotal] = useState(0);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [justReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'offers'; } catch (e) { return false; }
  });
  useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [crossAccounts, setCrossAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingCats, setSyncingCats] = useState(false);
  const [catsSyncedAt, setCatsSyncedAt] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [presaleFilter, setPresaleFilter] = useState("");
  const [sort, setSort] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [statusMsg, setStatusMsg] = useState("");
  const [bulkModal, setBulkModal] = useState<string|null>(null);
  const [bulkValue, setBulkValue] = useState("");
  const [priceMode, setPriceMode] = useState("set");
  const [bulkHandling, setBulkHandling] = useState("PT0S");
  const [showCopyWizard, setShowCopyWizard] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [relisting, setRelisting] = useState(false);
  const [relistProgress, setRelistProgress] = useState(0);
  const [activeTab, setActiveTab] = useState<"offers"|"promos"|"create"|"import"|"empik">("offers");
  const [editOffer, setEditOffer] = useState<{id:string, accountId:string, accountName:string} | null>(null);
  
  const setActiveTabWithContext = (tab: "offers"|"promos"|"create"|"import"|"empik") => {
    setActiveTab(tab);
    localStorage.setItem('assistant_context', tab === 'promos' ? 'offers:promos' : 'offers');
  };
  const LIMIT = 50;
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    localStorage.setItem('assistant_context', 'offers');
    return () => { localStorage.removeItem('assistant_context'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    axios.get(API + "/allegro/accounts").then(r => { setAccounts(r.data || []); setAccountsLoaded(true); }).catch(() => setAccountsLoaded(true));
    axios.get(API + "/offers/cross-accounts").then(r => setCrossAccounts(r.data?.accounts || [])).catch(() => {});
    axios.get(API + "/settings/app").then(r => {
      if (r.data.stock_alert_threshold) setStockThreshold(parseInt(r.data.stock_alert_threshold) || 20);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let sortParam = sort;
      if (sort === "price") sortParam = sortDir === "desc" ? "price_desc" : "price_asc";
      else if (sort === "stock") sortParam = sortDir === "desc" ? "stock_desc" : "stock_asc";
      else if (sort === "status") sortParam = sortDir === "desc" ? "status_desc" : "status_asc";
      else if (sort === "account") sortParam = sortDir === "desc" ? "account_desc" : "account_asc";
      else if (sort === "name") sortParam = sortDir === "desc" ? "name_desc" : "name";
      const res = await axios.get(API + "/offers", {
        params: { search, account_id: accountFilter, status: statusFilter, presale: presaleFilter, skip: page * LIMIT, limit: LIMIT, sort: sortParam, stock_threshold: stockThreshold }
      });
      setOffers(res.data.items);
      setTotal(res.data.total);
      setLowStockCount(res.data.low_stock_count || 0);
    } catch(e) { setStatusMsg("Blad ladowania"); }
    setLoading(false);
  }, [search, accountFilter, statusFilter, presaleFilter, page, sort, sortDir]);

  useEffect(() => { load(); }, [load]);

  const doSearch = () => { setSearch(searchInput); setPage(0); };

  const handleSort = (col: string) => {
    if (sort === col) setSortDir((d: string) => d === "asc" ? "desc" : "asc");
    else { setSort(col); setSortDir("asc"); }
    setPage(0);
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sort !== col) return <span style={{ color: "#d1d5db", marginLeft: 3, fontSize: 10 }}>&#8597;</span>;
    return <span style={{ color: "#1d4ed8", marginLeft: 3, fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const syncCategories = async () => {
    if (!window.confirm("Synchronizacja pobierze wszystkie kategorie Allegro. Może trwać 2-3 minuty. Kontynuować?")) return;
    setSyncingCats(true);
    try {
      const r = await axios.post(API + "/allegro/categories/sync", {}, { headers: { Authorization: "Bearer " + (localStorage.getItem("token")||"") } });
      const now = new Date().toLocaleString("pl-PL");
      setCatsSyncedAt(now);
      alert("Zsynchronizowano " + r.data.count + " kategorii.");
    } catch(e) { alert("Błąd synchronizacji kategorii"); }
    setSyncingCats(false);
  };
  const syncOffers = async () => {
    setSyncing(true); setStatusMsg("Synchronizuje...");
    try {
      const r = await axios.post(API + "/offers/sync");
      setStatusMsg("OK: " + r.data.total + " ofert"); load();
    } catch(e) { setStatusMsg("Blad"); }
    setSyncing(false); setTimeout(() => setStatusMsg(""), 4000);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAll = () => {
    if (selected.size === offers.length) setSelected(new Set());
    else setSelected(new Set(offers.map(o => o.id)));
  };

  const bulkUpdate = async () => {
    setProcessing(true);
    try {
      const value = bulkModal === "handling_time" ? bulkHandling : bulkValue;
      const pm = bulkModal === "handling_time" ? "set" : priceMode;
      const r = await axios.post(API + "/offers/bulk-update", { offer_ids: Array.from(selected), action: bulkModal, value, price_mode: pm });
      setStatusMsg("OK: " + r.data.ok + " zmieniono, bledow: " + r.data.error);
      setBulkModal(null); setBulkValue(""); setSelected(new Set()); load();
    } catch(e) { setStatusMsg("Blad"); }
    setProcessing(false); setTimeout(() => setStatusMsg(""), 4000);
  };

  // Wznawianie ofert POJEDYNCZO - z paskiem postepu. Kazda oferta osobno,
  // dzieki czemu widac realny procent i bledy nie zatrzymuja reszty.
  const doRelist = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm("Wznowić " + ids.length + " ofert? (ustawi Aktywne, bezterminowo, stan 0→100)")) return;
    setRelisting(true);
    setRelistProgress(0);
    const token = localStorage.getItem("token") || "";
    let ok = 0, err = 0;
    const errs: any[] = [];
    for (let i = 0; i < ids.length; i++) {
      try {
        const r: any = await axios.post(API + "/offers/bulk-update",
          { offer_ids: [ids[i]], action: "relist", value: "", price_mode: "set" },
          { headers: { Authorization: "Bearer " + token } });
        ok += r.data?.ok || 0;
        err += r.data?.error || 0;
        if (r.data?.errors && r.data.errors.length) errs.push(...r.data.errors);
      } catch (e) {
        err += 1;
        errs.push({ offer_id: ids[i], error: "Blad polaczenia" });
      }
      const done = i + 1;
      const pct = Math.round((done / ids.length) * 100);
      setRelistProgress(pct);
      setStatusMsg("⏳ Wznawiam " + done + "/" + ids.length + " (" + pct + "%)");
    }
    setStatusMsg("✓ Wznowiono " + ok + (err ? ", nie udało się " + err : ""));
    if (err && errs.length) {
      const lines = errs.map((x:any) => "• " + (x.offer_id||"?") + ": " + (x.error||"nieznany błąd")).join("\n");
      alert("Nie udało się wznowić " + err + " ofert:\n\n" + lines + "\n\nUWAGA: oferty zarchiwizowane (starsze niż 60 dni od zakończenia) trzeba wystawić na nowo — Allegro nie pozwala ich wznowić.");
    }
    setRelisting(false);
    setSelected(new Set());
    load();
    setTimeout(() => setStatusMsg(""), 6000);
  };

  // Formatuje date zakonczenia oferty: "10.08.2026 (9 dni temu)"
  const formatEndedAt = (iso?: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const dni = Math.floor((Date.now() - d.getTime()) / 86400000);
    let kiedy = "";
    if (dni <= 0) kiedy = "dzisiaj";
    else if (dni === 1) kiedy = "wczoraj";
    else kiedy = dni + " dni temu";
    return dd + "." + mm + "." + yyyy + " (" + kiedy + ")";
  };

  const totalPages = Math.ceil(total / LIMIT);
  const s: any = {
    btn: { border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 },
    input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, outline: "none" },
    th: { cursor: "pointer", userSelect: "none" as const, display: "flex", alignItems: "center" },
  };

  return (
    <>
      {((accountsLoaded && accounts.length === 0) || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby zarządzać, wystawiać i edytować oferty, podłącz konto Allegro."
          returnModule="offers"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
    <div style={{ padding: isMobile ? 8 : 16, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {onBack && <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>&#8592;</button>}
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Zarzadzanie ofertami</h1>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{total} ofert</div>
          </div>
        </div>
        {activeTab === "offers" && (
          <button onClick={syncOffers} disabled={syncing} style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
            {syncing ? "..." : "Synchronizuj oferty"}
          </button>
        )}
        {activeTab === "create" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <button onClick={syncCategories} disabled={syncingCats} style={{ ...s.btn, background: syncingCats ? "#f3f4f6" : "#1d4ed8", color: syncingCats ? "#374151" : "#fff" }}>
              {syncingCats ? "Synchronizuję..." : "🔄 Synchronizuj kategorie"}
            </button>
            {catsSyncedAt && <div style={{ fontSize: 10, color: "#9ca3af" }}>Ostatnia sync: {catsSyncedAt}</div>}
          </div>
        )}
      </div>

      {/* Zakladki */}
      <div style={{ display: "flex", gap: 2, marginBottom: 14, borderBottom: "2px solid #e5e7eb" }}>
        <button onClick={() => setActiveTabWithContext("offers")} style={{ border: "none", background: "none", padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: activeTab === "offers" ? "#1d4ed8" : "#6b7280", borderBottom: activeTab === "offers" ? "2px solid #1d4ed8" : "2px solid transparent", marginBottom: -2 }}>
          Oferty
        </button>
        <button onClick={() => setActiveTabWithContext("promos")} style={{ border: "none", background: "none", padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: activeTab === "promos" ? "#1d4ed8" : "#6b7280", borderBottom: activeTab === "promos" ? "2px solid #1d4ed8" : "2px solid transparent", marginBottom: -2 }}>
          ⭐ Wyróżnione
        </button>
      <button onClick={() => setActiveTabWithContext("create")} style={{ border: "none", background: "none", padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: activeTab === "create" ? "#1d4ed8" : "#6b7280", borderBottom: activeTab === "create" ? "2px solid #1d4ed8" : "2px solid transparent", marginBottom: -2 }}>
          ➕ Wystaw
        </button>
        <button onClick={() => setActiveTabWithContext("import")} style={{ border: "none", background: "none", padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: activeTab === "import" ? "#1d4ed8" : "#6b7280", borderBottom: activeTab === "import" ? "2px solid #1d4ed8" : "2px solid transparent", marginBottom: -2 }}>
          🤝 Kontrahent
        </button>
        <button onClick={() => setActiveTabWithContext("empik")} style={{ border: "none", background: "none", padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: activeTab === "empik" ? "#1d4ed8" : "#6b7280", borderBottom: activeTab === "empik" ? "2px solid #1d4ed8" : "2px solid transparent", marginBottom: -2 }}>
          🛒 Empik
        </button>
      </div>

      {activeTab === "create" && <OfferCreate onBack={() => setActiveTabWithContext("offers")} />}
      {editOffer && <OfferEdit offerId={editOffer.id} accountId={editOffer.accountId} accountName={editOffer.accountName}
        onClose={() => setEditOffer(null)} onSaved={() => { setEditOffer(null); load(); }} />}
      {activeTab === "promos" && <OfferPromos accounts={accounts} />}
      {activeTab === "import" && <div style={{ display: "flex", flexDirection: "column" as const, gap: 16 }}>
        <ImportSharedModal currentUser={currentUser} asPanel={true} onDone={() => { load(); }} />
        <CompareAccounts currentUser={currentUser} />
      </div>}

      {activeTab === "empik" && <EmpikOffers />}
      {activeTab === "offers" && <>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, flex: isMobile ? "1 1 100%" : "1 1 280px" }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
              placeholder="Szukaj nazwy, SKU, EAN, nr oferty..." style={{ ...s.input, flex: 1 }} />
            <button onClick={doSearch} style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>Szukaj</button>
          </div>
          <select value={accountFilter} onChange={e => { setAccountFilter(e.target.value); setPage(0); }} style={s.input}>
            <option value="">Wszystkie konta</option>
            {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
            {crossAccounts.map((a: any) => <option key={a.id} value={a.id}>↗ {a.name} (obce)</option>)}
          </select>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={s.input}>
            <option value="">Wszystkie statusy</option>
            <option value="ACTIVE">Aktywne</option>
            <option value="INACTIVE">Nieaktywne</option>
            <option value="ENDED">Zakonczone</option>
          </select>
          <select value={presaleFilter} onChange={e => { setPresaleFilter(e.target.value); setPage(0); }} style={s.input}>
            <option value="">Wszystkie</option>
            <option value="true">Z opoznieniem</option>
            <option value="false">Natychmiast</option>
          </select>
          {search && <button onClick={() => { setSearch(""); setSearchInput(""); setPage(0); }}
            style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>x</button>}
        </div>
      </div>

      {selected.size > 0 && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 12px", marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" as const }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>Zaznaczono: {selected.size}</span>
          <button onClick={() => setBulkModal("stock")} style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>Zmien stan</button>
          <button onClick={() => setShowCopyWizard(true)} style={{ ...s.btn, background: "#0891b2", color: "#fff" }}>📋 Kopiuj na konto</button>
          <button onClick={() => setBulkModal("price")} style={{ ...s.btn, background: "#15803d", color: "#fff" }}>Zmien cene</button>
          <button onClick={() => setBulkModal("handling_time")} style={{ ...s.btn, background: "#7c3aed", color: "#fff" }}>Czas wysylki</button>
          <button onClick={() => { if (window.confirm("Zakończyć " + selected.size + " ofert?")) { axios.post(API + "/offers/bulk-update", { offer_ids: Array.from(selected), action: "status", value: "ENDED", price_mode: "set" }, { headers: { Authorization: "Bearer " + (localStorage.getItem("token")||"") } }).then(() => { setSelected(new Set()); load(); }); } }} style={{ ...s.btn, background: "#dc2626", color: "#fff" }}>Zakończ</button>
          <button onClick={doRelist} disabled={relisting} style={{ ...s.btn, background: relisting ? "#9ca3af" : "#059669", color: "#fff", cursor: relisting ? "default" : "pointer" }}>{relisting ? "⏳ Wznawiam " + relistProgress + "%" : "♻️ Wznów oferty"}</button>
          <button onClick={() => {
            const ids = Array.from(selected).join("\n");
            const blob = new Blob([ids], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "numery_ofert.txt";
            a.click();
            URL.revokeObjectURL(url);
          }} style={{ ...s.btn, background: "#0369a1", color: "#fff" }}>nr Ofert TXT</button>
          <button onClick={() => setSelected(new Set())} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>Odznacz</button>
        </div>
      )}

      {relisting && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: relistProgress + "%", background: "#059669", transition: "width 0.2s ease" }} />
          </div>
        </div>
      )}
      {statusMsg && <div style={{ fontSize: 12, color: "#15803d", fontWeight: 600, marginBottom: 8 }}>{statusMsg}</div>}

      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: "36px 56px 1fr 90px 85px 65px 130px 90px 55px", gap: 8, padding: "10px 12px", background: "#f9fafb", borderBottom: "2px solid #e5e7eb", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" as const }}>
            <div><input type="checkbox" checked={selected.size === offers.length && offers.length > 0} onChange={selectAll} /></div>
            <div>Foto</div>
            <div style={s.th} onClick={() => handleSort("name")}>Nazwa/SKU <SortIcon col="name" /></div>
            <div style={s.th} onClick={() => handleSort("account")}>Konto <SortIcon col="account" /></div>
            <div style={s.th} onClick={() => handleSort("price")}>Cena <SortIcon col="price" /></div>
            <div style={s.th} onClick={() => handleSort("stock")}>Stan <SortIcon col="stock" /></div>
            <div style={s.th} onClick={() => handleSort("handling")}>Wysylka <SortIcon col="handling" /></div>
            <div style={s.th} onClick={() => handleSort("status")}>Status <SortIcon col="status" /></div>
            <div>Link</div>
          </div>
        )}
        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <input type="checkbox" checked={selected.size === offers.length && offers.length > 0} onChange={selectAll} />
            <span style={{ fontSize: 11, color: "#6b7280" }}>Zaznacz wszystkie</span>
          </div>
        )}
        {lowStockCount > 0 && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ padding: '6px 14px', color: '#dc2626', fontSize: 12, fontWeight: 500 }}>
              ⚠️ {lowStockCount} ofert ma stan ≤ {stockThreshold} szt.
            </div>
          </div>
        )}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center" as const, color: "#9ca3af" }}>Ladowanie...</div>
        ) : offers.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center" as const, color: "#9ca3af" }}>Brak ofert</div>
        ) : offers.map(o => {
          const delayed = isDelayed(o.handling_time);
          const lowStock = o.stock_available <= stockThreshold;
          const rowBg = delayed ? "#fdf4ff" : lowStock && o.stock_available > 0 ? "#fff7ed" : "#fff";
          const shipDate = handlingDate(o.handling_time);
          if (isMobile) return (
            <div key={o.id} style={{ padding: "10px 12px", borderBottom: "1px solid #f3f4f6", background: rowBg }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} style={{ marginTop: 4, flexShrink: 0 }} />
                {o.image_url ? (
                  <img src={o.image_url} alt="" style={{ width: 50, height: 50, objectFit: "cover" as const, borderRadius: 6, flexShrink: 0, border: "1px solid #e5e7eb" }} />
                ) : (
                  <div style={{ width: 50, height: 50, background: "#f3f4f6", borderRadius: 6, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 2 }}>{o.name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>
                    {o.sku && <CopyableField label="SKU" value={o.sku} />}
                    {o.ean && o.ean !== "N/A" && <CopyableField label="EAN" value={o.ean} />}
                    <CopyableField label="nr" value={o.offer_id} muted />
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" as const, alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{o.price?.toFixed(2)} {o.currency}</span>
                    <span style={{ fontSize: 11, borderRadius: 4, padding: "1px 6px", fontWeight: 600, background: o.status === "ACTIVE" ? "#f0fdf4" : "#fef2f2", color: o.status === "ACTIVE" ? "#15803d" : "#dc2626" }}>{o.status === "ACTIVE" ? "Aktywna" : o.status}</span>
                    {o.status === "ENDED" && o.ended_at && <span style={{ fontSize: 10, color: "#9ca3af", display: "block", marginTop: 2 }}>{formatEndedAt(o.ended_at)}</span>}
                    {delayed && <span style={{ fontSize: 11, background: "#f3e8ff", color: "#7c3aed", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>{handlingLabel(o.handling_time)}{shipDate ? " (od " + shipDate + ")" : ""}</span>}
                    {o.cross_published ? <span style={{ fontSize: 11, color: "#c2410c", background: "#ffedd5", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>↗ {o.cross_target_name} (obca firma)</span> : <span style={{ fontSize: 11, color: "#9ca3af" }}>{o.account_name}</span>}
                    <a href={o.offer_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1d4ed8" }}>Link</a>
                    <button onClick={() => setEditOffer({id: o.offer_id, accountId: o.account_id, accountName: o.account_name})}
                      style={{ fontSize: 10, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", cursor: "pointer", color: "#374151" }}>✏️ Edytuj</button>
                  </div>
                </div>
              </div>
            </div>
          );
          return (
            <div key={o.id} style={{ display: "grid", gridTemplateColumns: "36px 56px 1fr 90px 85px 65px 130px 90px 55px", gap: 8, padding: "8px 12px", borderBottom: "1px solid #f3f4f6", alignItems: "center", background: rowBg, fontSize: 12 }}>
              <div><input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} /></div>
              <div>{o.image_url ? <img src={o.image_url} alt="" style={{ width: 46, height: 46, objectFit: "cover" as const, borderRadius: 4, border: "1px solid #e5e7eb" }} /> : <div style={{ width: 46, height: 46, background: "#f3f4f6", borderRadius: 4 }} />}</div>
              <div>
                <div style={{ fontWeight: 500, color: "#111827", marginBottom: 1 }}>{o.name}</div>
                <div style={{ color: "#6b7280", fontSize: 11 }}>{o.sku && <CopyableField label="SKU" value={o.sku} />}{o.ean && o.ean !== "N/A" && <CopyableField label="EAN" value={o.ean} />}<CopyableField label="nr" value={o.offer_id} muted /></div>
              </div>
              <div style={{ fontSize: 11 }}>{o.cross_published ? <span style={{ color: "#c2410c", fontWeight: 600 }}>↗ {o.cross_target_name} (obca firma)</span> : o.account_name}</div>
              <div style={{ fontWeight: 600 }}>{o.price?.toFixed(2)} {o.currency}</div>
              <div style={{ fontWeight: 600, color: o.stock_available <= stockThreshold ? "#dc2626" : "#15803d" }}>{o.stock_available}{o.stock_available <= 20 && <span style={{ fontSize: 10, display: "block" }}>!</span>}</div>
              <div>{delayed ? <div><span style={{ fontSize: 11, background: "#f3e8ff", color: "#7c3aed", borderRadius: 4, padding: "2px 5px", fontWeight: 600, display: "inline-block", marginBottom: 1 }}>{handlingLabel(o.handling_time)}</span>{shipDate && <div style={{ fontSize: 10, color: "#7c3aed" }}>od {shipDate}</div>}</div> : <span style={{ fontSize: 11, color: "#9ca3af" }}>natychmiast</span>}</div>
              <div><span style={{ fontSize: 11, borderRadius: 4, padding: "2px 5px", fontWeight: 600, background: o.status === "ACTIVE" ? "#f0fdf4" : "#fef2f2", color: o.status === "ACTIVE" ? "#15803d" : "#dc2626" }}>{o.status}</span>{o.status === "ENDED" && o.ended_at && <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 6 }}>{formatEndedAt(o.ended_at)}</span>}</div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <a href={o.offer_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1d4ed8", textDecoration: "none" }}>Link</a>
                <button onClick={() => setEditOffer({id: o.offer_id, accountId: o.account_id, accountName: o.account_name})}
                  style={{ fontSize: 10, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", cursor: "pointer", color: "#374151" }}>✏️</button>
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12, flexWrap: "wrap" as const, alignItems: "center" }}>
          <button onClick={() => setPage(0)} disabled={page === 0} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", opacity: page === 0 ? 0.4 : 1 }}>&#171;</button>
          <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", opacity: page === 0 ? 0.4 : 1 }}>&#8249;</button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i : Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
            return <button key={p} onClick={() => setPage(p)} style={{ ...s.btn, background: p === page ? "#1d4ed8" : "#f3f4f6", color: p === page ? "#fff" : "#374151", border: "1px solid #d1d5db", minWidth: 32 }}>{p+1}</button>;
          })}
          <button onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page >= totalPages-1} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", opacity: page >= totalPages-1 ? 0.4 : 1 }}>&#8250;</button>
          <button onClick={() => setPage(totalPages-1)} disabled={page >= totalPages-1} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", opacity: page >= totalPages-1 ? 0.4 : 1 }}>&#187;</button>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>Str. {page+1}/{totalPages} ({total})</span>
        </div>
      )}
      </>
      }

      {bulkModal && bulkModal !== "copy" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: isMobile ? "92vw" : 400, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {bulkModal === "stock" && "Zmien stan (" + selected.size + " ofert)"}
              {bulkModal === "price" && "Zmien cene (" + selected.size + " ofert)"}
              {bulkModal === "handling_time" && "Czas wysylki (" + selected.size + " ofert)"}
            </div>
            {bulkModal === "stock" && <div><label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 6 }}>Nowy stan (szt.)</label><input type="number" min="0" value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...s.input, width: "100%", boxSizing: "border-box" as const }} placeholder="np. 100" /></div>}
            {bulkModal === "price" && (
              <div>
                <label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 6 }}>Tryb</label>
                <select value={priceMode} onChange={e => setPriceMode(e.target.value)} style={{ ...s.input, width: "100%", boxSizing: "border-box" as const, marginBottom: 10 }}>
                  <option value="set">Ustaw dokladna cene</option>
                  <option value="add">Podwyz o kwote PLN</option>
                  <option value="subtract">Obniz o kwote PLN</option>
                  <option value="percent">Zmien o procent</option>
                </select>
                <input type="number" step="0.01" value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...s.input, width: "100%", boxSizing: "border-box" as const }} placeholder={priceMode === "percent" ? "np. -5 lub 10" : "np. 29.99"} />
              </div>
            )}
            {bulkModal === "handling_time" && (
              <div>
                <label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 6 }}>Czas wysylki</label>
                <select value={bulkHandling} onChange={e => setBulkHandling(e.target.value)} style={{ ...s.input, width: "100%", boxSizing: "border-box" as const }}>
                  {HANDLING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value === "PT0S" ? "Natychmiast" : o.label + " opoznienia"}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={bulkUpdate} disabled={processing} style={{ ...s.btn, background: "#1d4ed8", color: "#fff", flex: 1, padding: "10px" }}>{processing ? "Przetwarzam..." : "Zastosuj (" + selected.size + ")"}</button>
              <button onClick={() => setBulkModal(null)} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>Anuluj</button>
            </div>
          </div>
        </div>
      )}
    </div>
    {showCopyWizard && <CopyToAccountWizard offerIds={Array.from(selected)} accounts={accounts} currentUser={currentUser} onClose={() => setShowCopyWizard(false)} onDone={() => { setSelected(new Set()); load(); }} />}
    </>
  );
}
